import { useMutation, useReactiveVar } from '@apollo/client';
import {
  SearchRequestInput,
  TripBreakdownExportFilterInput,
} from 'common/types/globalTypes';
import React from 'react';
import { TripBreakdownItem } from '..';
import {
  TripBreakdownExportFilter,
  TripBreakdownExportFilterVariables,
} from './__generated__/TripBreakdownExportFilter';
import { FilterArrayType } from 'common/components/Filters';
import { toFilterVariableExport } from './utils';
import {
  AccountingExportCycleFn,
  AccountingExportToCsv,
} from 'common/components/AccountingExportToCsv';
import { toSelection } from 'common/utils/entity';

import TRIP_BREAKDOWN_EXPORT_FILTER from './TripBreakdownExportFilter.graphql';
import { travelerDialogVisibleVar } from 'utility/cache/ui';

interface ExportTripsProps {
  selectedRows: TripBreakdownItem[];
  isSelectedAll: boolean;
  searchFilters: FilterArrayType[] | undefined;
  tripBreakdownDocumentSearchRequest: SearchRequestInput | null;
  isLinkSearch: boolean;
}

export const ExportTrips: React.FC<ExportTripsProps> = ({
  selectedRows,
  isSelectedAll,
  searchFilters,
  tripBreakdownDocumentSearchRequest,
  isLinkSearch,
}) => {
  const travelerDialogVisibleDefault = useReactiveVar(travelerDialogVisibleVar);

  const [tripBreakdownExportFilter] = useMutation<
    TripBreakdownExportFilter,
    TripBreakdownExportFilterVariables
  >(TRIP_BREAKDOWN_EXPORT_FILTER);

  const getExportCycle: AccountingExportCycleFn = async (
    desc,
    acctPeriod,
    includeDist
  ) => {
    let exportVariable: TripBreakdownExportFilterInput = {
      exportDescription: desc,
      corporatePeriodId: acctPeriod,
      isDistributionIncluded: includeDist,
    };

    if (isSelectedAll) {
      const filterVariable = toFilterVariableExport(searchFilters ?? []);
      exportVariable = {
        ...exportVariable,
        searchRequest: tripBreakdownDocumentSearchRequest,
        isLinkSearch,
        filterTravelerId: travelerDialogVisibleDefault,
        ...filterVariable,
      };
    } else if (selectedRows.length > 0) {
      exportVariable = {
        ...exportVariable,
        entityDelete: selectedRows.map(toSelection),
      };
    }

    const { errors, data } = await tripBreakdownExportFilter({
      variables: { input: exportVariable },
    });

    return {
      id: data?.tripBreakdownExportFilter?.exportResult?.exportCycles?.[0],
      errors,
    };
  };

  return (
    <AccountingExportToCsv
      isButtonVisible={isSelectedAll || selectedRows.length > 0}
      title="Enter the description and press confirm to download the Trip worksheet."
      getExportCycle={getExportCycle}
    />
  );
};
